import React, { useMemo } from 'react';
import {
    PieChart, Pie, Cell,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { isSystemID, getUserDivision } from '../utils/userUtils';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#f43f5e', '#8b5cf6']; // Modern aesthetic palette

const AnalyticsWidgets = ({ tasks, getUserName, users = [], activeDivision = '', compact = false, layout = 'grid' }) => {


    // 1. Completion Rate (Gauge-like using Pie)
    const completionData = useMemo(() => {
        const completed = tasks.filter(t => t.Status === 'Completed').length;
        const total = tasks.length;

        return [
            { name: 'Completed', value: completed },
            { name: 'Remaining', value: total - completed }
        ];
    }, [tasks]);

    const completionRate = useMemo(() => {
        const completed = tasks.filter(t => t.Status === 'Completed').length;
        const total = tasks.length;
        return total > 0 ? Math.round((completed / total) * 100) : 0;
    }, [tasks]);

    // 2. Status Breakdown
    const statusData = useMemo(() => {
        const counts = {};
        tasks.forEach(t => {
            counts[t.Status] = (counts[t.Status] || 0) + 1;
        });
        return Object.keys(counts).map(status => ({
            name: status,
            value: counts[status]
        }));
    }, [tasks]);

    // 3. Workload by Assignee
    const workloadData = useMemo(() => {
        const counts = {};
        tasks.forEach(t => {
            const assigneeId = t.AssigneeID;

            // Filter 1: Exclude System IDs (Groups/Approval IDs)
            if (isSystemID(assigneeId)) return;

            // Filter 2: Exclude assignees from other divisions if activeDivision is provided
            if (activeDivision && users.length > 0) {
                const userDiv = getUserDivision(assigneeId, users);
                if (userDiv && userDiv !== activeDivision) return;
            }

            const assigneeName = getUserName(assigneeId, users);
            // Truncate long names for chart but preserve honorifics
            let displayName = assigneeName.includes('@') ? assigneeName.split('@')[0] : assigneeName;
            if (!assigneeName.includes('@')) {
                const parts = displayName.split(' ');
                const titles = [
                    'Dr.', 'Dr', 'Mr.', 'Mr', 'Mrs.', 'Mrs', 'Ms.', 'Ms', 'Prof.', 'Prof', 
                    'Shri', 'Sh.', 'Smt.', 'Smt', 'Mohd.', 'Mohd', 'Md.', 'Md', 
                    'Cmde', 'Cmdr', 'Capt.', 'Capt'
                ];
                if (parts.length > 1 && titles.includes(parts[0])) {
                    displayName = `${parts[0]} ${parts[1]}`;
                } else {
                    displayName = parts[0];
                }
            }
            counts[displayName] = (counts[displayName] || 0) + 1;
        });

        return Object.keys(counts)
            .map(name => ({ name, count: counts[name] }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5); // Top 5
    }, [tasks, getUserName, users, activeDivision]);

    // 4. Priority Distribution
    const priorityData = useMemo(() => {
        const counts = { High: 0, Medium: 0, Low: 0, Normal: 0 }; // Initialize known priorities
        tasks.forEach(t => {
            const p = t.Priority || 'Normal';
            counts[p] = (counts[p] || 0) + 1;
        });
        return Object.keys(counts).map(p => ({
            subject: p,
            A: counts[p],
            fullMark: tasks.length
        }));
    }, [tasks]);

    const chartHeight = compact ? 160 : (layout === 'horizontal' ? 180 : 220); // Increased compact height for better visibility
    const titleStyle = { margin: '0 0 0.5rem 0', color: '#5f6368', fontSize: '0.9rem', fontWeight: 600, textAlign: 'center' };
    const widgetStyle = {
        background: 'white',
        padding: '1.25rem',
        borderRadius: '12px',
        border: '1px solid #f1f5f9', // Very soft border
        minHeight: 'auto',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)' // Premium floating shadow effect
    };

    const containerStyle = layout === 'horizontal' ? {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)', // 4 Columns
        gap: '1rem',
        height: '100%'
    } : {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)', // Default 2x2
        gap: compact ? '0.75rem' : '1.5rem',
        marginTop: compact ? '0' : '2rem',
        height: '100%'
    };

    return (
        <div style={containerStyle}>
            {tasks.length === 0 && (
                <div style={{ gridColumn: '1 / -1', padding: '2rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                    No analytics data available for this division yet.
                </div>
            )}
            {tasks.length > 0 && (
                <>
                    {/* ... widgets ... (rest of render code) */}

                    {/* Completion Rate Widget */}
                    <div style={widgetStyle}>
                        <h4 style={titleStyle}>Completion Rate</h4>
                        <div style={{ position: 'relative', height: `${chartHeight}px`, width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={completionData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={layout === 'horizontal' ? 40 : (compact ? 35 : 60)}
                                        outerRadius={layout === 'horizontal' ? 60 : (compact ? 50 : 80)}
                                        fill="#8884d8"
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {completionData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={index === 0 ? '#10b981' : '#e2e8f0'} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={{
                                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#202124' }}>{completionRate}%</div>
                            </div>
                        </div>
                    </div>

                    {/* Status Breakdown Widget */}
                    <div style={widgetStyle}>
                        <h4 style={titleStyle}>Status Distribution</h4>
                        <div style={{ height: `${chartHeight}px`, width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={statusData}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={layout === 'horizontal' ? 60 : (compact ? 50 : 80)}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {statusData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ fontSize: '0.8rem' }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Workload Widget */}
                    <div style={widgetStyle}>
                        <h4 style={titleStyle}>Workload Distribution</h4>
                        <div style={{ height: `${chartHeight}px`, width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={workloadData} margin={{ bottom: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis 
                                        dataKey="name" 
                                        tick={{ fontSize: 9, angle: -35, textAnchor: 'end' }} 
                                        interval={0} 
                                        height={60}
                                    />
                                    <YAxis allowDecimals={false} hide />
                                    <Tooltip cursor={{ fill: '#f5f5f5' }} contentStyle={{ fontSize: '0.8rem' }} />
                                    <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={28} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Priority Radar Widget */}
                    <div style={widgetStyle}>
                        <h4 style={titleStyle}>Priority Focus</h4>
                        <div style={{ height: `${chartHeight}px`, width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={priorityData}>
                                    <PolarGrid />
                                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} />
                                    <PolarRadiusAxis angle={30} domain={[0, 'auto']} hide />
                                    <Radar name="Tasks" dataKey="A" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.6} />
                                    <Tooltip contentStyle={{ fontSize: '0.8rem' }} />
                                </RadarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                </>
            )}
        </div>
    );
};

export default AnalyticsWidgets;
